import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, InputAdornment, type SxProps, type Theme, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT, FONT_SIZE, ICON_SIZE, INK, LETTER_SPACING, LINE_HEIGHT, NEUTRAL } from "@/theme/tokens";
import { RoundedCheckboxState } from "@/ui/Clickable/RoundedCheckboxBox";
import { SystemFlatSwitch } from "@/ui/Clickable/SystemFlatSwitch";
import { EmptyState } from "@/ui/molecules/EmptyState";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { SystemValidationTextField } from "../SystemValidationTextField";
import { CountBadge } from "./CountBadge";
import { DropdownMenuActions } from "./DropdownMenuActions";
import { DropdownCheckbox, DropdownRow, ROW_HEIGHT, rowHeightFor, rowHeightsFor } from "./DropdownRow";
import { DropdownVirtualList, type VirtualEntry } from "./DropdownVirtualList";
import type { DropdownMenuAction, DropdownPanelCounts, DropdownPanelLook, Extracted, NormalizedItem } from "./types";
import type { HighlightSegment } from "./useDropdownSearch";
import { filterBySegment, filterCollapsedGroups, toOptionIds } from "./visibleOptions";

type CreateNewOption = { enabled: boolean; label: string; onCreate: (inputValue: string) => void };

type Props<T> = {
	multiple: boolean;
	hideCheckbox: boolean;
	normalizedItems: NormalizedItem<T>[];
	filteredItems: NormalizedItem<T>[];
	effectiveIds: Set<string>;
	selectableIds: string[];
	idOf: (value: Extracted<T>) => string;
	onToggleId: (id: string) => void;
	onToggleVisible: (visibleIds: string[]) => void;
	/** Toggle the given group children. Receives only the currently visible, enabled ids (never headers/disabled). */
	onToggleGroup: (groupIds: string[]) => void;
	onClearAll: () => void;

	/** Render a collapse chevron on each group header (opt-in `collapsibleGroups`). */
	collapsibleGroups: boolean;
	/** React keys of the group headers whose children are currently hidden. */
	collapsedGroupKeys: ReadonlySet<string>;
	onToggleGroupCollapsed: (groupKey: string) => void;

	searchEnabled: boolean;
	/** Show a loading indicator instead of the empty state while items are being fetched. */
	loading?: boolean;
	/** Already-translated footnote under the list; the function form receives the panel's live counts. */
	panelNote?: ReactNode | ((counts: DropdownPanelCounts) => ReactNode);
	query: string;
	onQueryChange: (value: string) => void;
	searchPlaceholder: string;
	getHighlightSegments: (label: string) => HighlightSegment[];

	highlightedId: string | null;

	allOptionLabel: string | null;
	showSelectedOnly: boolean;
	/** "All" / "Selected" segment — owned by the parent so the keyboard hook navigates the same rows the panel renders. */
	segment: "all" | "selected";
	onSegmentChange: (segment: "all" | "selected") => void;

	commitApply: boolean;
	onDone: () => void;
	createNewOption?: CreateNewOption;

	/** Preferred list cap; the effective cap is the smaller of this and the real on-screen space. */
	maxMenuHeight: number;
	/** Total vertical space the panel may occupy (viewport-derived). Undefined on the sheet (it owns its own height). */
	availableHeight?: number;
	/** Width styling for the desktop panel wrapper (fixed for multi/`menuWidth`, fit-content for single). */
	widthSx?: SxProps<Theme>;
	isSheet: boolean;
	/** id wired to the trigger's `aria-controls` (L10). */
	listboxId?: string;
	/** `SystemDropdown`'s `panelLook`. */
	look: DropdownPanelLook;
	/** `SystemDropdown`'s `panelTitle` (the `menu` look only). */
	panelTitle?: string;
	/** `SystemDropdown`'s `menuActions` (the `menu` look only), and how a chosen one, or a row's `rowAction`, closes the panel. */
	menuActions?: DropdownMenuAction[];
	closeForCommand: () => void;
	dataTestId?: string;
};

/** The `menu` look's small uppercase heading — a section title or the panel's own — as the design system's `pop-title`. */
const MENU_TITLE_SX = {
	display: "flex",
	alignItems: "center",
	boxSizing: "border-box",
	px: 1.25,
	pt: 1,
	pb: 0.75,
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 600,
	letterSpacing: LETTER_SPACING.LABEL,
	textTransform: "uppercase",
	color: INK.MUTED,
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
} as const;

/** The keys that move the keyboard's row (`useDropdownKeyboard`). */
const NAVIGATION_KEYS: ReadonlySet<string> = new Set(["ArrowDown", "ArrowUp", "Home", "End"]);

/** The `menu` look's rule between two sections: a hairline with 6px either side, inset 4px from the panel's edges. */
const MENU_SEPARATOR_MARGIN_PX = 6;
const MENU_SEPARATOR_PX = 2 * MENU_SEPARATOR_MARGIN_PX + 1;
const MENU_SEPARATOR_SX = { height: "1px", my: `${MENU_SEPARATOR_MARGIN_PX}px`, mx: 0.5, backgroundColor: NEUTRAL[200] } as const;

/** The `menu` look's search: no outline, the glyph a notch in from the edge, the text a half-step under the rows'. */
const MENU_SEARCH_ICON_SX = { fontSize: ICON_SIZE.MICRO, color: INK.MUTED, marginInlineStart: 0.75 } as const;
const MENU_SEARCH_INPUT_SX = {
	height: CONTROL_HEIGHT.COMPACT,
	fontSize: FONT_SIZE.EMPHASIS,
	"& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
} as const;

export const DropdownPanel = <T,>(props: Props<T>) => {
	const { t } = useTranslation();
	// Read ONCE here and thread it down: react-window is told each row's height up front, so the value the
	// list measures with and the one the row paints with have to be the same read.
	const isCoarsePointer = useMediaQuery("(pointer: coarse)");
	const isMenu = props.look === "menu";
	const rowHeights = rowHeightsFor(isCoarsePointer, props.look);
	const headerHeight = rowHeights.group;
	const [createValue, setCreateValue] = useState<string | null>(null);
	// A menu opens with nothing washed, as the design system's menu: the keyboard's row shows once the arrows move it
	// or a search picks the row Enter would choose. The panel mounts on every open, so this starts unset each time. On the
	// document, because the keys reach the panel's focus trap (its parent) as often as the search box inside it.
	const [keyboardMoved, setKeyboardMoved] = useState(false);
	useEffect(() => {
		// Only the menu look hides the keyboard row until the keys move; a standard panel needs no listener.
		if (!isMenu) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (NAVIGATION_KEYS.has(event.key)) setKeyboardMoved(true);
		};
		document.addEventListener("keydown", onKeyDown, true);
		return () => document.removeEventListener("keydown", onKeyDown, true);
	}, [isMenu]);

	const {
		multiple,
		hideCheckbox,
		filteredItems,
		effectiveIds,
		selectableIds,
		idOf,
		onToggleId,
		onToggleVisible,
		onToggleGroup,
		onClearAll,
		collapsibleGroups,
		collapsedGroupKeys,
		onToggleGroupCollapsed,
		searchEnabled,
		loading,
		panelNote,
		query,
		onQueryChange,
		searchPlaceholder,
		getHighlightSegments,
		highlightedId,
		allOptionLabel,
		showSelectedOnly,
		segment,
		onSegmentChange,
		commitApply,
		onDone,
		createNewOption,
		maxMenuHeight,
		availableHeight,
		widthSx,
		isSheet,
		listboxId,
		dataTestId,
	} = props;

	// Measure the flex slot the list ACTUALLY gets. When the panel is height-capped on a short screen, the
	// slot shrinks (footer/search are flex-shrink:0), so react-window sizes to the real space left rather than
	// a fixed number. Init to maxMenuHeight so the first paint isn't zero-height while the observer warms up.
	const listSlotRef = useRef<HTMLDivElement>(null);
	const [listSlot, setListSlot] = useState<number>(maxMenuHeight);
	useEffect(() => {
		const el = listSlotRef.current;
		if (!el) return;
		const measure = () => setListSlot(el.clientHeight);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// Visible/actionable rows via the shared helper — the SAME predicate the parent feeds the keyboard hook,
	// so the highlight can never target a row the panel didn't render (disabled/segment-hidden).
	const visibleItems = useMemo(
		() => filterBySegment(filteredItems, showSelectedOnly, segment, effectiveIds),
		[showSelectedOnly, segment, filteredItems, effectiveIds],
	);
	const visibleOptionIds = useMemo(() => toOptionIds(visibleItems), [visibleItems]);

	// Rows that actually paint. Collapse is applied HERE and not to `visibleItems` above, because it is a display
	// concern: "select all" and each group header's own toggle keep a collapsed group's children in scope, which
	// is what stops a fold from moving any checkbox.
	const renderedItems = useMemo(() => filterCollapsedGroups(visibleItems, collapsedGroupKeys, idOf), [visibleItems, collapsedGroupKeys, idOf]);

	const selectedCount = useMemo(() => selectableIds.filter((id) => effectiveIds.has(id)).length, [selectableIds, effectiveIds]);
	const totalCount = selectableIds.length;

	// The listbox's active option (keyboard highlight), surfaced to AT via the search input's aria-activedescendant.
	const activeDescendantId = highlightedId && listboxId ? `${listboxId}-opt-${highlightedId}` : undefined;

	const visibleSelectedCount = useMemo(() => visibleOptionIds.filter((id) => effectiveIds.has(id)).length, [visibleOptionIds, effectiveIds]);
	const selectAllState =
		visibleSelectedCount === 0
			? RoundedCheckboxState.Off
			: visibleSelectedCount === visibleOptionIds.length
				? RoundedCheckboxState.On
				: RoundedCheckboxState.Indeterminate;

	// ---- flat entries for the virtualized list ----
	const entries = useMemo<VirtualEntry[]>(() => {
		// M3: precompute id → visible-index once (O(n)) instead of an indexOf per row (O(n²)).
		const optionIndexById = new Map(visibleOptionIds.map((id, index) => [id, index]));
		const visibleOptionIdSet = new Set(visibleOptionIds);
		// Ids belonging to any group header (its `groupValues`) — child rows of a group are indented so the
		// header reads as a parent with nested options.
		const groupedChildIds = new Set<string>();
		for (const it of renderedItems) if (it.groupValues) for (const v of it.groupValues) groupedChildIds.add(idOf(v));
		const built: VirtualEntry[] = [];
		for (const item of renderedItems) {
			if (item.groupValues) {
				// Only the group's children that are currently visible AND actionable (the set already excludes
				// headers + disabled). Header state and the click target both use THIS set so a group toggle under
				// an active search/filter never reaches hidden or disabled rows.
				const groupVisibleIds = item.groupValues.map((v) => idOf(v)).filter((id) => visibleOptionIdSet.has(id));
				// L10: don't render a group header whose children are all filtered out (no empty headers).
				if (groupVisibleIds.length === 0) continue;
				const groupSelected = groupVisibleIds.every((id) => effectiveIds.has(id));
				const groupPartial = !groupSelected && groupVisibleIds.some((id) => effectiveIds.has(id));
				const groupState = groupSelected ? RoundedCheckboxState.On : groupPartial ? RoundedCheckboxState.Indeterminate : RoundedCheckboxState.Off;
				const toggleGroup = () => onToggleGroup(groupVisibleIds);
				const collapsed = collapsedGroupKeys.has(item.key);
				const headerTestId = dataTestId ? `${dataTestId}-group-${item.key}` : undefined;
				// A menu's section title only heads its items: no band, no select-all, nothing to press. Every section after
				// the first is ruled off from the one above, as the design system's `pop-sep`.
				if (isMenu) {
					const ruled = built.some((entry) => entry.key.startsWith("group-"));
					built.push({
						key: `group-${item.key}`,
						height: headerHeight + (ruled ? MENU_SEPARATOR_PX : 0),
						render: (style: CSSProperties) => (
							<Box key={item.key} style={style} role="presentation" data-testid={headerTestId}>
								{ruled && <Box sx={MENU_SEPARATOR_SX} />}
								<Box sx={[MENU_TITLE_SX, { height: headerHeight }]}>{item.label}</Box>
							</Box>
						),
					});
					continue;
				}
				built.push({
					key: `group-${item.key}`,
					height: headerHeight,
					render: (style: CSSProperties) => {
						// Section band: a tinted, hairline-separated header so the group reads as a parent above its
						// (indented) children rather than as just another bold row.
						const bandSx = {
							display: "flex",
							alignItems: "center",
							gap: 1.25,
							px: 1.75,
							boxSizing: "border-box",
							height: headerHeight,
							backgroundColor: NEUTRAL[50],
							borderBottom: `1px solid ${NEUTRAL[150]}`,
						};
						const labelNode = (
							<Box
								component="span"
								sx={{
									fontSize: FONT_SIZE.CAPTION,
									fontWeight: 700,
									color: INK.SECONDARY,
									whiteSpace: "nowrap",
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{item.label}
							</Box>
						);
						// Collapse affordance. The header row itself is the group's select-all, so the chevron has to stop
						// its click from reaching it; preventDefault on mousedown keeps the popper from pulling focus off
						// the search input mid-click.
						const collapseToggle = collapsibleGroups && (
							<Box
								component="span"
								role="button"
								tabIndex={0}
								aria-expanded={!collapsed}
								aria-label={collapsed ? t("system.dropdown.expandGroup") : t("system.dropdown.collapseGroup")}
								data-testid={headerTestId ? `${headerTestId}-collapse` : undefined}
								onMouseDown={(event) => {
									event.preventDefault();
									event.stopPropagation();
								}}
								onClick={(event) => {
									event.stopPropagation();
									onToggleGroupCollapsed(item.key);
								}}
								onKeyDown={(event) => {
									if (event.key !== "Enter" && event.key !== " ") return;
									event.preventDefault();
									event.stopPropagation();
									onToggleGroupCollapsed(item.key);
								}}
								// `alignSelf: stretch` + the side padding is the tap target: the glyph is 18px, but on a coarse
								// pointer the row is TOUCH_TARGET_MIN tall, and an 18px hit area inside it is a miss waiting
								// to happen — stretching makes the target the full height of the band it sits in.
								sx={{
									display: "inline-flex",
									alignItems: "center",
									alignSelf: "stretch",
									flex: "0 0 auto",
									px: 0.5,
									marginInlineStart: -1,
									color: INK.MUTED,
									cursor: "pointer",
								}}
							>
								{collapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
							</Box>
						);
						// Single-select: the group is a non-interactive PARENT label (like the department dropdown) —
						// you pick one option, there is no "select all in group". Only multi-select makes the header a
						// real toggle button (focusable + Enter/Space, tri-state conveyed to AT; checkbox is aria-hidden).
						if (!multiple) {
							return (
								<Box key={item.key} style={style} role="presentation" data-testid={headerTestId} sx={{ ...bandSx, cursor: "default" }}>
									{collapseToggle}
									{item.startAdornment}
									{labelNode}
								</Box>
							);
						}
						return (
							<Box
								key={item.key}
								style={style}
								role="button"
								tabIndex={0}
								aria-pressed={groupState === RoundedCheckboxState.Indeterminate ? "mixed" : groupState === RoundedCheckboxState.On}
								aria-label={item.labelText}
								data-testid={headerTestId}
								onClick={toggleGroup}
								onKeyDown={(event) => {
									if (event.key === "Enter" || event.key === " ") {
										event.preventDefault();
										toggleGroup();
									}
								}}
								sx={{ ...bandSx, cursor: "pointer" }}
							>
								{collapseToggle}
								{item.startAdornment}
								{!hideCheckbox && <DropdownCheckbox state={groupState} />}
								{labelNode}
							</Box>
						);
					},
				});
				continue;
			}
			// Enabled options index 0..n (testids stay stable); disabled rows aren't in the map, so key their
			// testid off item.key instead of colliding on `-menu-item-0`.
			const index = optionIndexById.get(item.id);
			const rowTestId = dataTestId ? (index !== undefined ? `${dataTestId}-menu-item-${index}` : `${dataTestId}-menu-item-disabled-${item.key}`) : undefined;
			built.push({
				key: item.key,
				id: item.id,
				height: rowHeightFor(item, isCoarsePointer, props.look),
				render: (style: CSSProperties) => (
					<DropdownRow
						key={item.key}
						id={listboxId ? `${listboxId}-opt-${item.id}` : undefined}
						item={item}
						multiple={multiple}
						hideCheckbox={hideCheckbox}
						selected={effectiveIds.has(item.id)}
						highlighted={highlightedId === item.id && (!isMenu || keyboardMoved || query !== "")}
						highlightSegments={getHighlightSegments(item.labelText)}
						onToggle={onToggleId}
						style={style}
						dataTestId={rowTestId}
						indent={groupedChildIds.has(item.id)}
						coarse={isCoarsePointer}
						look={props.look}
						onRowAction={(action) => {
							props.closeForCommand();
							action.onSelect();
						}}
					/>
				),
			});
		}
		return built;
	}, [
		renderedItems,
		visibleOptionIds,
		multiple,
		hideCheckbox,
		effectiveIds,
		highlightedId,
		getHighlightSegments,
		onToggleId,
		onToggleGroup,
		collapsibleGroups,
		collapsedGroupKeys,
		onToggleGroupCollapsed,
		idOf,
		dataTestId,
		listboxId,
		isCoarsePointer,
		headerHeight,
		isMenu,
		props.look,
		keyboardMoved,
		query,
		t,
		props.closeForCommand,
	]);

	// Effective list cap = the smaller of the caller's preferred height and the real on-screen slot, floored at
	// two rows so a very short viewport still shows something scrollable rather than collapsing to nothing.
	// The floor stays on the FINE-pointer row height: it is a pre-existing "never collapse to nothing"
	// guarantee, and scaling it with the coarse row height would push the list further past a short
	// `listSlot` (whose parent clips) than it already goes.
	const listMaxHeight = Math.max(ROW_HEIGHT.single * 2, Math.min(maxMenuHeight, listSlot));

	const searchRegion = searchEnabled && (
		<Box
			sx={[
				{ px: 1.5, pt: 1.25, pb: 1, flexShrink: 0 },
				// The menu's search is a borderless row ruled off from the list, flush with the panel's rounded top.
				isMenu && { mx: -1, mt: -1, mb: 0.5, px: 1, pt: 1, pb: 0.75, borderBottom: `1px solid ${NEUTRAL[200]}` },
			]}
		>
			<SystemValidationTextField
				dataTestId={dataTestId ? `${dataTestId}-filter` : undefined}
				value={query}
				onChange={(event) => onQueryChange(event.target.value)}
				placeholder={searchPlaceholder}
				reserveHeightForHelperText={false}
				fullWidth
				autoFocus
				onClear={query ? () => onQueryChange("") : undefined}
				injectedProps={{
					slotProps: {
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon sx={isMenu ? MENU_SEARCH_ICON_SX : { fontSize: 18, color: NEUTRAL[500] }} />
								</InputAdornment>
							),
							...(isMenu ? { sx: MENU_SEARCH_INPUT_SX } : {}),
						},
						// Native input is the combobox that drives the listbox: label it, point it at the listbox, and
						// expose the keyboard-highlighted option so screen readers announce the active row on arrow nav (L10).
						htmlInput: {
							role: "combobox",
							"aria-label": searchPlaceholder,
							"aria-expanded": true,
							"aria-controls": listboxId,
							"aria-autocomplete": "list",
							"aria-activedescendant": activeDescendantId,
						},
					},
				}}
			/>
		</Box>
	);

	const segmentedRegion = multiple && showSelectedOnly && (
		<Box sx={{ mx: 1.5, mb: 1, alignSelf: "flex-start", flexShrink: 0 }}>
			<SystemFlatSwitch<"all" | "selected">
				data-testid={dataTestId ? `${dataTestId}-segment` : undefined}
				variant="filled"
				// The segment gates what the list shows, so apply immediately (no delayed callback).
				delayCallback={0}
				activeIndexOrValue={segment}
				onSwitch={(option) => onSegmentChange(option.value)}
				fontSizeOverrides={{ xs: FONT_SIZE.CAPTION, lg: FONT_SIZE.CAPTION, xl: FONT_SIZE.CAPTION, xxl: FONT_SIZE.CAPTION }}
				options={[
					{ label: t("system.dropdown.all"), value: "all", "data-testid": dataTestId ? `${dataTestId}-segment-all` : undefined },
					{
						label: (
							<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
								{t("system.dropdown.selected")}
								<CountBadge count={selectedCount} />
							</Box>
						),
						value: "selected",
						"data-testid": dataTestId ? `${dataTestId}-segment-selected` : undefined,
					},
				]}
			/>
		</Box>
	);

	const selectAllRegion = multiple && allOptionLabel !== null && (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.75, pt: 0.5, pb: 1.25, borderBottom: `1px solid ${NEUTRAL[150]}`, flexShrink: 0 }}>
			<Box
				component="button"
				type="button"
				role="checkbox"
				aria-checked={selectAllState === RoundedCheckboxState.Indeterminate ? "mixed" : selectAllState === RoundedCheckboxState.On}
				aria-label={query ? t("system.dropdown.selectAllFiltered") : (allOptionLabel ?? undefined)}
				onClick={() => onToggleVisible(visibleOptionIds)}
				data-testid={dataTestId ? `${dataTestId}-select-all` : undefined}
				sx={{ display: "flex", alignItems: "center", gap: 1.25, border: 0, background: "none", cursor: "pointer", font: "inherit", p: 0 }}
			>
				{!hideCheckbox && <DropdownCheckbox state={selectAllState} />}
				<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY }}>
					{query ? t("system.dropdown.selectAllFiltered") : allOptionLabel}
				</Box>
				<CountBadge count={query ? visibleOptionIds.length : totalCount} />
			</Box>
			<Box
				component="button"
				type="button"
				disabled={selectedCount === 0}
				onClick={onClearAll}
				data-testid={dataTestId ? `${dataTestId}-clear-all` : undefined}
				sx={(theme) => ({
					marginInlineStart: "auto",
					border: 0,
					background: "none",
					font: "inherit",
					fontSize: FONT_SIZE.SECONDARY,
					fontWeight: 600,
					borderRadius: 1,
					px: 0.75,
					py: 0.25,
					cursor: selectedCount === 0 ? "default" : "pointer",
					color: selectedCount === 0 ? NEUTRAL[400] : theme.palette.primary.dark,
					"&:hover": { backgroundColor: selectedCount === 0 ? "transparent" : alpha(theme.palette.primary.main, 0.1) },
				})}
			>
				{t("system.dropdown.clearAll")}
			</Box>
		</Box>
	);

	const listRegion =
		loading && entries.length === 0 ? (
			// Distinguish "still fetching" from a definitive empty result (spec: loading is its own state).
			<EmptyState
				dataTestId={dataTestId ? `${dataTestId}-loading` : undefined}
				icon={<CircularProgress size={20} />}
				title={t("system.dropdown.loading")}
				sx={{ py: 3 }}
			/>
		) : entries.length === 0 ? (
			<EmptyState
				dataTestId={dataTestId ? `${dataTestId}-empty` : undefined}
				icon={<SearchIcon />}
				title={query ? t("system.dropdown.noMatches", { query }) : t("system.dropdown.noOptions")}
				sx={{ py: 3 }}
			/>
		) : (
			<DropdownVirtualList entries={entries} maxHeight={listMaxHeight} scrollToId={highlightedId} />
		);

	// Visually-hidden live region so AT announces the running selection total (the visible footer count) on
	// every toggle — otherwise a screen-reader user hears each row's state but never the total (L10).
	const liveRegion = multiple && (
		<Box aria-live="polite" sx={visuallyHidden}>
			{selectedCount} {t("system.dropdown.selectedCount", { count: selectedCount })}
		</Box>
	);

	const createRegion =
		createNewOption &&
		(createValue === null ? (
			<Box sx={{ px: 1, py: 0.5, borderTop: `1px solid ${NEUTRAL[150]}`, flexShrink: 0 }}>
				<SystemButton
					buttonVariant="borderless"
					disabled={!createNewOption.enabled}
					leadingIcon={<AddIcon fontSize="inherit" />}
					onClick={() => setCreateValue("")}
					data-testid={dataTestId ? `${dataTestId}-create-new` : undefined}
					sx={{ width: "100%", justifyContent: "flex-start", fontSize: FONT_SIZE.EMPHASIS }}
				>
					{createNewOption.label}
				</SystemButton>
			</Box>
		) : (
			<Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${NEUTRAL[150]}`, flexShrink: 0 }}>
				<SystemValidationTextField
					autoFocus
					dataTestId={dataTestId ? `${dataTestId}-create-new-input` : undefined}
					value={createValue}
					reserveHeightForHelperText={false}
					label={createNewOption.label}
					inputSx={{ fontSize: FONT_SIZE.EMPHASIS }}
					onChange={(event) => setCreateValue(event.target.value)}
					// Enter commits the new value; stopPropagation so it doesn't bubble to the panel keyboard hook
					// (which would toggle the highlighted row instead of creating).
					onKeyDown={(event) => {
						if (event.key === "Enter") {
							event.stopPropagation();
							event.preventDefault();
							if (createValue.trim()) createNewOption.onCreate(createValue.trim());
							setCreateValue(null);
						}
					}}
					inputProps={{
						onBlur: () => {
							if (createValue.trim()) createNewOption.onCreate(createValue.trim());
							setCreateValue(null);
						},
					}}
				/>
			</Box>
		));

	const footerLabel = (
		<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, fontWeight: 500, whiteSpace: "nowrap" }}>
			<Box component="b" sx={{ color: INK.PRIMARY, fontWeight: 700 }}>
				{selectedCount}
			</Box>{" "}
			{t("system.dropdown.selectedCount", { count: selectedCount })}
		</Box>
	);
	const footerDone = (
		<SystemButton
			data-testid={dataTestId ? `${dataTestId}-done` : undefined}
			onClick={onDone}
			// Bottom-sheet primary action: a comfortable 48px full-width tap target (via the component's own
			// mobile-full-width prop, which owns the `xs` width); desktop keeps the compact inline button.
			size={isSheet ? "medium" : "small"}
			mobileFullWidthPercentage={isSheet ? "100%" : undefined}
			sx={isSheet ? undefined : { marginInlineStart: "auto" }}
		>
			{commitApply ? t("system.dropdown.apply") : t("system.dropdown.done")}
		</SystemButton>
	);
	// Sits under the rows and above the footer: it explains the DEFAULT selection, so it has to read as part
	// of this list rather than as a caption on the form behind it.
	const noteContent = typeof panelNote === "function" ? panelNote({ shown: filteredItems.length, total: props.normalizedItems.length, query }) : panelNote;
	const noteRegion = noteContent ? (
		<Box
			data-testid={dataTestId ? `${dataTestId}-note` : undefined}
			sx={[
				{ px: 1.75, py: 1, borderTop: `1px solid ${NEUTRAL[150]}`, fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, flexShrink: 0 },
				// A menu's note is a quiet line of its own, as the design system's `pop-note`.
				isMenu && { px: 1.25, pt: 0.25, pb: 1, borderTop: 0, fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.DENSE_PROSE },
			]}
		>
			{noteContent}
		</Box>
	) : null;

	// A live menu commits every tick as it happens, so it carries no count / Done footer (the sheet keeps its Done).
	const footerRegion =
		(multiple || commitApply) &&
		!(isMenu && !commitApply && !isSheet) &&
		// On the mobile sheet the count sits on its own line above a full-width Done, so a narrow viewport can't
		// squeeze the count into a wrap or oversize the button. Desktop keeps the single inline count … Done row.
		(isSheet ? (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					px: 1.75,
					py: 1.25,
					borderTop: `1px solid ${NEUTRAL[150]}`,
					backgroundColor: NEUTRAL[50],
					flexShrink: 0,
				}}
			>
				{footerLabel}
				{footerDone}
			</Box>
		) : (
			<Box
				sx={{ display: "flex", alignItems: "center", px: 1.75, py: 1.25, borderTop: `1px solid ${NEUTRAL[150]}`, backgroundColor: NEUTRAL[50], flexShrink: 0 }}
			>
				{footerLabel}
				{footerDone}
			</Box>
		));

	const body: ReactNode = (
		// Cap the whole panel to the viewport-derived height (desktop only; the sheet passes undefined and owns
		// its own height). The list region is the only flex-shrinkable child, so it — not the footer — absorbs it.
		<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, maxHeight: availableHeight }}>
			{searchRegion}
			{isMenu && props.panelTitle !== undefined && (
				<Box sx={[MENU_TITLE_SX, { flexShrink: 0 }]} data-testid={dataTestId ? `${dataTestId}-title` : undefined}>
					{props.panelTitle}
				</Box>
			)}
			{isMenu && noteRegion}
			{liveRegion}
			{segmentedRegion}
			{selectAllRegion}
			{/* The listbox wraps only the option rows — search/toggles/footer are siblings, not listbox children (valid ARIA). */}
			<Box
				ref={listSlotRef}
				id={listboxId}
				role="listbox"
				aria-label={t("system.dropdown.select")}
				aria-multiselectable={multiple}
				aria-activedescendant={activeDescendantId}
				// flex "0 1 auto": content-sized when it fits, shrinks when the panel is height-capped. `overflow:
				// hidden` clips during the one-frame convergence before react-window re-sizes to the measured slot.
				sx={{ flex: "0 1 auto", minHeight: 0, overflow: "hidden" }}
			>
				{listRegion}
			</Box>
			{createRegion}
			{isMenu && props.menuActions !== undefined && props.menuActions.length > 0 && (
				<DropdownMenuActions actions={props.menuActions} onPicked={props.closeForCommand} />
			)}
			{!isMenu && noteRegion}
			{footerRegion}
		</Box>
	);

	if (isSheet) return body;

	return <Box sx={[{ display: "flex", flexDirection: "column" }, widthSx ?? { width: 320, maxWidth: "92vw" }] as SxProps<Theme>}>{body}</Box>;
};
